import { useState } from 'react';
import NumberInput from '../../../components/NumberInput';
import { ProficiencyLevel, getAbilityModifier, getProficiencyBonus } from '../../../components/dnd/abilities/Ability';
import ProficiencyToggle from '../../../components/dnd/abilities/ProficiencyToggle';
import { useAuth } from '../../../hooks/useAuth';
import Proficiencies, { ProficiencyData } from '../Proficiencies';
import ProficiencyBonus from '../ProficiencyBonus';
import HeroicInspiration from '../HeroicInspiration';

type AbilityData = { level: number; scores: Record<string, number>; proficiencies: Record<string, ProficiencyLevel> };
type AbilityScoresProps = { characterId: number; data: AbilityData; level: number; className?: string; onChange?: (data: AbilityData) => void; proficiencies: ProficiencyData; onProficiencyChange: (field: keyof ProficiencyData, value: string) => void; proficiencyBonus?: number | null; onBonusChange: (value: number) => void; heroicInspiration?: boolean; onInspirationChange?: (value: boolean) => void };
const abilities = [
    ['Strength', 'strength', ['Athletics']],
    ['Dexterity', 'dexterity', ['Acrobatics', 'Sleight of Hand', 'Stealth']],
    ['Constitution', 'constitution', []],
    ['Intelligence', 'intelligence', ['Arcana', 'History', 'Investigation', 'Nature', 'Religion']],
    ['Wisdom', 'wisdom', ['Animal Handling', 'Insight', 'Medicine', 'Perception', 'Survival']],
    ['Charisma', 'charisma', ['Deception', 'Intimidation', 'Performance', 'Persuasion']],
] as const;
const signed = (value: number) => value >= 0 ? `+${value}` : `${value}`;

function AbilityScores({ characterId, data, level, className = '', onChange, proficiencies, onProficiencyChange, proficiencyBonus, onBonusChange, heroicInspiration = false, onInspirationChange }: AbilityScoresProps) {
    const { apiRequest } = useAuth();
    const [state, setState] = useState({ ...data, level });
    const [error, setError] = useState('');
    const bonus = proficiencyBonus ?? getProficiencyBonus(level);
    const save = (next: AbilityData) => {
        setState(next);
        onChange?.(next);
        setError('');
        apiRequest(`/api/characters/${characterId}/abilities`, 'PUT', next)
            .catch(() => setError('Your changes could not be saved. Please try again.'));
    };
    const proficiencyRow = (name: string, key: string, ability: string) => {
        const proficiency = state.proficiencies[key] || 0;
        return <div className={`parchment-skill ${proficiency ? 'is-proficient' : ''}`} key={key}>
            <ProficiencyToggle value={proficiency} ariaLabel={`${name} proficiency`} onChange={(value) => save({ ...state, proficiencies: { ...state.proficiencies, [key]: value as ProficiencyLevel } })} />
            <span>{name}</span>
            <strong aria-label={`${name} modifier`}>{signed(getAbilityModifier(state.scores[ability] ?? 10) + proficiency * bonus)}</strong>
        </div>;
    };
    return <div className={`abilities-layout ${className}`}>
        {error && <p className="alert alert-danger" role="alert">{error}</p>}
        <div className="ability-badges">
            {abilities.map(([name, key]) => <label className={`ability-badge ${state.proficiencies[`save.${key}`] ? 'is-proficient' : ''}`} key={key}>
                <span title={name}>{key.slice(0, 3).toUpperCase()}</span>
                <NumberInput aria-label={`${name} score`} value={state.scores[key] ?? 10} onValueChange={(score) => {
                    if (Number.isFinite(score)) save({ ...state, scores: { ...state.scores, [key]: score } });
                }} />
                <strong>{signed(getAbilityModifier(state.scores[key] ?? 10))}</strong>
            </label>)}
        </div>
        <div className="sheet-detail-grid">
            <section className="parchment-panel skills-panel">
                <header className="panel-heading"><h2><i className="fa-solid fa-leaf" aria-hidden="true" /> Skills</h2><ProficiencyBonus characterId={characterId} value={bonus} onChange={onBonusChange} /></header>
                <h3 className="skills-subheading"><i className="fa-solid fa-shield-halved" aria-hidden="true" /> Saving Throws</h3>
                {abilities.map(([name, key]) => proficiencyRow(`${name} ST`, `save.${key}`, key))}
                <h3 className="skills-subheading skills-subheading--divider">Skills</h3>
                {abilities.flatMap(([, key, skills]) => skills.map(name => ({ name, key }))).sort((a, b) => a.name.localeCompare(b.name)).map(({ name, key }) => proficiencyRow(name, `skill.${name.toLowerCase().replace(/ /g, '_')}`, key))}
                <p className="panel-hint">Tap a marker to change proficiency.</p>
            </section>
            <div className="sheet-side-panels">
                <HeroicInspiration characterId={characterId} value={heroicInspiration} onChange={onInspirationChange} />
                <Proficiencies characterId={characterId} data={proficiencies} onChange={onProficiencyChange} />
            </div>
        </div>
    </div>;
}
export default AbilityScores;
